"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.DocumentSignatureModel = void 0;
/**
 * Model for managing document signatures via API
 *
 * Handles all signature-related operations including:
 * - Fetching signatures for a document
 * - Adding new signatures
 * - Checking signature status and completion
 */
var DocumentSignatureModel = /** @class */ (function () {
    function DocumentSignatureModel($http, APPURL) {
        this.$http = $http;
        this.APPURL = APPURL;
        this.URLS = {
            DOCUMENTS: "".concat(APPURL, "/api/v1/documents"),
        };
        this.data = null;
        this.extract = this.extract.bind(this);
        this.cacheResults = this.cacheResults.bind(this);
    }
    /**
     * Extract data from HTTP response
     */
    DocumentSignatureModel.prototype.extract = function (result) {
        return result.data;
    };
    /**
     * Cache and return results
     */
    DocumentSignatureModel.prototype.cacheResults = function (result) {
        this.data = this.extract(result);
        return this.data;
    };
    /**
     * Get all signatures for a document
     *
     * @param documentId - The document ID
     * @returns Promise resolving to signatures response
     *
     * @example
     * ```typescript
     * documentSignatureModel.getSignatures(123)
     *   .then(response => {
     *     console.log('Signatures:', response.signatures);
     *     console.log('Pending signatures:', response.pending_signatures);
     *     console.log('Completion:', response.completion_percentage + '%');
     *   });
     * ```
     */
    DocumentSignatureModel.prototype.getSignatures = function (documentId) {
        return this.$http
            .get("".concat(this.URLS.DOCUMENTS, "/").concat(documentId, "/signatures"))
            .then(this.cacheResults);
    };
    /**
     * Add a signature to a document
     *
     * @param documentId - The document ID
     * @param data - Signature data including base64 image, signee type, and field ID
     * @returns Promise resolving to signature creation response
     *
     * @example
     * ```typescript
     * const signatureData = {
     *   signature_data: 'data:image/png;base64,iVBORw0KGgo...',
     *   signee_type: 'student',
     *   document_field_id: 'field2' // Required - field ID from annotation
     * };
     *
     * documentSignatureModel.addSignature(123, signatureData)
     *   .then(response => {
     *     console.log(response.message);
     *     console.log('Is fully signed:', response.data.is_fully_signed);
     *     console.log('Completion:', response.data.completion_percentage + '%');
     *   })
     *   .catch(error => {
     *     console.error('Signature failed:', error);
     *   });
     * ```
     */
    DocumentSignatureModel.prototype.addSignature = function (documentId, data) {
        return this.$http
            .post("".concat(this.URLS.DOCUMENTS, "/").concat(documentId, "/signatures"), data)
            .then(this.cacheResults);
    };
    /**
     * Get signature status for a document
     *
     * Returns comprehensive information about signature requirements,
     * completion status, and pending signatures
     *
     * @param documentId - The document ID
     * @returns Promise resolving to signature status
     *
     * @example
     * ```typescript
     * documentSignatureModel.getSignatureStatus(123)
     *   .then(response => {
     *     const status = response.status;
     *     console.log('Signature required:', status.signature_required);
     *     console.log('Fully signed:', status.is_fully_signed);
     *     console.log('Completion:', status.completion_percentage + '%');
     *     console.log('Pending:', status.pending_signatures);
     *   });
     * ```
     */
    DocumentSignatureModel.prototype.getSignatureStatus = function (documentId) {
        return this.$http
            .get("".concat(this.URLS.DOCUMENTS, "/").concat(documentId, "/signature-status"))
            .then(this.cacheResults);
    };
    /**
     * Check if a specific signee type has pending signatures for a document
     *
     * @param documentId - The document ID
     * @param signeeType - The signee type to check ('student', 'guardian', etc.)
     * @returns Promise resolving to boolean indicating if signatures are pending
     *
     * @example
     * ```typescript
     * documentSignatureModel.hasPendingSignatures(123, 'student')
     *   .then(hasPending => {
     *     if (hasPending) {
     *       console.log('Student has pending signatures');
     *     }
     *   });
     * ```
     */
    DocumentSignatureModel.prototype.hasPendingSignatures = function (documentId, signeeType) {
        return this.getSignatureStatus(documentId).then(function (response) {
            var pending = response.status.pending_signatures;
            // Check if any field requires this signee type
            return Object.values(pending.fields).some(function (fieldSigneeType) { return fieldSigneeType === signeeType; });
        });
    };
    /**
     * Get count of pending signatures for a specific signee type
     *
     * @param documentId - The document ID
     * @param signeeType - The signee type to count ('student', 'guardian', etc.)
     * @returns Promise resolving to count of pending signatures
     *
     * @example
     * ```typescript
     * documentSignatureModel.getPendingSignatureCount(123, 'guardian')
     *   .then(count => {
     *     console.log(`Guardian has ${count} pending signatures`);
     *   });
     * ```
     */
    DocumentSignatureModel.prototype.getPendingSignatureCount = function (documentId, signeeType) {
        return this.getSignatureStatus(documentId).then(function (response) {
            var pending = response.status.pending_signatures;
            // Count fields that require this signee type
            return Object.values(pending.fields).filter(function (fieldSigneeType) { return fieldSigneeType === signeeType; }).length;
        });
    };
    /**
     * Filter signatures by signee type
     *
     * @param signatures - Array of signatures to filter
     * @param signeeType - The signee type to filter by
     * @returns Filtered array of signatures
     */
    DocumentSignatureModel.prototype.filterSignaturesByType = function (signatures, signeeType) {
        return signatures.filter(function (signature) { return signature.signee_type === signeeType; });
    };
    /**
     * Check if all required signatures are complete
     *
     * @param documentId - The document ID
     * @returns Promise resolving to boolean indicating completion
     *
     * @example
     * ```typescript
     * documentSignatureModel.isFullySigned(123)
     *   .then(isComplete => {
     *     if (isComplete) {
     *       console.log('Document is fully signed!');
     *     }
     *   });
     * ```
     */
    DocumentSignatureModel.prototype.isFullySigned = function (documentId) {
        return this.getSignatureStatus(documentId).then(function (response) { return response.status.is_fully_signed; });
    };
    /**
     * Get completion percentage for a document
     *
     * @param documentId - The document ID
     * @returns Promise resolving to completion percentage (0-100)
     *
     * @example
     * ```typescript
     * documentSignatureModel.getCompletionPercentage(123)
     *   .then(percentage => {
     *     console.log(`Document is ${percentage}% signed`);
     *   });
     * ```
     */
    DocumentSignatureModel.prototype.getCompletionPercentage = function (documentId) {
        return this.getSignatureStatus(documentId).then(function (response) { return response.status.completion_percentage; });
    };
    /**
     * Validate signature request data before submission
     *
     * @param data - Signature request data to validate
     * @returns Validation result with errors if any
     */
    DocumentSignatureModel.prototype.validateSignatureRequest = function (data) {
        var errors = [];
        if (!data.signature_data || data.signature_data.trim() === "") {
            errors.push("Signature data is required");
        }
        if (!data.signee_type || data.signee_type.trim() === "") {
            errors.push("Signee type is required");
        }
        if (!data.document_field_id) {
            errors.push("Document field ID is required");
        }
        var validTypes = ["student", "guardian", "instructor", "staff"];
        if (data.signee_type &&
            !validTypes.includes(data.signee_type.toLowerCase())) {
            errors.push("Invalid signee type. Must be one of: ".concat(validTypes.join(", ")));
        }
        // Check if signature_data looks like base64 or data URL
        if (data.signature_data && !data.signature_data.includes("base64")) {
            errors.push("Signature data must be base64 encoded");
        }
        return {
            valid: errors.length === 0,
            errors: errors,
        };
    };
    return DocumentSignatureModel;
}());
exports.DocumentSignatureModel = DocumentSignatureModel;
DocumentSignatureModel.$inject = ["$http", "APPURL"];
exports.default = DocumentSignatureModel;
